import { Callout } from './parts';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import GroupsRounded from '@mui/icons-material/GroupsRounded';
import WorkspacePremiumRounded from '@mui/icons-material/WorkspacePremiumRounded';
import EventRepeatRounded from '@mui/icons-material/EventRepeatRounded';
import HistoryRounded from '@mui/icons-material/HistoryRounded';
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError, unwrap } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { DataTable, type Column } from '../../components/DataTable';
import { EmptyState, ErrorState, LoadingRows } from '../../components/states';
import { StatusChip } from '../../components/StatusChip';
import { Field, Panel } from '../../components/ui';
import { businessDate, date, dateTime, plural } from '../../lib/format';
import { errorMessage, useNotify } from '../../lib/notify';
import {
  correctableEvent,
  EVENT_LABELS,
  extendedPaidThrough,
  fetchPlans,
  fetchSubscription,
  isTrial,
  MAX_GRACE_DAYS,
  MIN_GRACE_DAYS,
  reversedIds,
  tenantKeys,
  type CorrectableEvent,
  type SubscriptionDetail,
  type SubscriptionEvent,
  type SubscriptionStatus,
  type TenantDetail,
} from './api';
import { FieldGrid, StatusChipFor } from './parts';

const intIn = (v: string, min: number, max: number) => {
  if (!/^\d+$/.test(v.trim())) return null;
  const n = Number(v);
  return n >= min && n <= max ? n : null;
};

function usePlans(enabled: boolean) {
  return useQuery({ queryKey: tenantKeys.plans, queryFn: fetchPlans, enabled });
}

/**
 * Direct extension (no code): days and/or a plan, an optional payment reference, and a reason.
 * One Idempotency-Key per opening, so a retry after a network error never extends twice.
 */
export function ExtendDialog({
  open,
  tenant,
  subscription,
  onClose,
}: {
  open: boolean;
  tenant: Pick<TenantDetail, 'id' | 'name'>;
  subscription: SubscriptionStatus;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const notify = useNotify();
  const plans = usePlans(open);
  const activePlans = (plans.data?.data ?? []).filter((p) => p.isActive);
  const [key, setKey] = useState(() => crypto.randomUUID());
  const [days, setDays] = useState('30');
  const [planId, setPlanId] = useState('');
  const [paymentRef, setPaymentRef] = useState('');
  const [notes, setNotes] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const plan = activePlans.find((p) => p.id === planId);
  const daysN = days.trim() === '' && plan ? null : intIn(days, 1, 3660);
  const effectiveDays = daysN ?? plan?.durationDays ?? null;
  const daysInvalid = !(days.trim() === '' && plan) && daysN === null;
  const notesInvalid = notes.trim().length === 0;

  const reset = () => {
    setKey(crypto.randomUUID());
    setDays('30');
    setPlanId('');
    setPaymentRef('');
    setNotes('');
    setSubmitted(false);
    setError(null);
  };
  const close = () => {
    if (busy) return;
    reset();
    onClose();
  };

  const submit = () => {
    setSubmitted(true);
    if (daysInvalid || notesInvalid || effectiveDays === null) return;
    setBusy(true);
    setError(null);
    unwrap(
      api.POST('/platform/tenants/{id}/subscription/extend', {
        params: { path: { id: tenant.id }, header: { 'idempotency-key': key } },
        body: {
          ...(daysN !== null ? { days: daysN } : {}),
          ...(plan ? { planId: plan.id } : {}),
          ...(paymentRef.trim() ? { paymentRef: paymentRef.trim() } : {}),
          notes: notes.trim(),
        },
      }),
    )
      .then((r) => {
        setBusy(false);
        notify.success(`Extended to ${businessDate(r.subscription?.paidThroughDate)}`);
        void qc.invalidateQueries({ queryKey: tenantKeys.all });
        reset();
        onClose();
      })
      .catch((e: unknown) => {
        setBusy(false);
        setError(e);
      });
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Extend subscription</DialogTitle>
        <DialogContent>
          <Stack spacing={2.25}>
            <Typography variant="body2" color="text.secondary">
              <strong>{tenant.name}</strong> is paid through{' '}
              {businessDate(subscription.paidThroughDate)}. Days are added from that date, or from
              today if it has passed.
            </Typography>
            {error !== null && <Callout severity="error">{errorMessage(error)}</Callout>}
            <TextField
              select
              label="Plan (optional)"
              value={planId}
              onChange={(e) => {
                setPlanId(e.target.value);
                // A plan brings its own duration; custom days are the fallback without one.
                setDays(e.target.value ? '' : '30');
              }}
              helperText={
                plan
                  ? 'Recorded as a renewal. Leave days empty to use the plan’s duration.'
                  : 'Without a plan these are extra days; a shop on its trial stays on trial.'
              }
            >
              <MenuItem value="">No plan — custom days</MenuItem>
              {activePlans.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name} · {plural(p.durationDays, 'day')}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Days to add"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              placeholder={plan ? String(plan.durationDays) : undefined}
              slotProps={{ htmlInput: { inputMode: 'numeric' } }}
              error={submitted && daysInvalid}
              helperText={submitted && daysInvalid ? 'Enter 1–3660 days.' : ' '}
              required={!plan}
            />
            <TextField
              label="Payment reference (optional)"
              value={paymentRef}
              onChange={(e) => setPaymentRef(e.target.value)}
              slotProps={{ htmlInput: { maxLength: 100 } }}
            />
            <TextField
              label="Reason"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              minRows={2}
              required
              slotProps={{ htmlInput: { maxLength: 500 } }}
              error={submitted && notesInvalid}
              helperText={
                submitted && notesInvalid
                  ? 'A reason is required. It is saved in the history and audit log.'
                  : 'Saved in the history and audit log.'
              }
            />
            {effectiveDays !== null && !daysInvalid && (
              <Callout severity="info" icon={<EventRepeatRounded fontSize="small" />}>
                Adds {plural(effectiveDays, 'day')}: paid through about{' '}
                <strong>
                  {date(extendedPaidThrough(subscription.paidThrough, effectiveDays).toISOString())}
                </strong>
                . Takes effect immediately.
              </Callout>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="outlined" onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" loading={busy}>
            Extend
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

/**
 * Moves a trial shop to a paid plan once it has paid: a plan renewal (RENEWED) with the payment
 * reference. The paid period starts after the trial days left, or today.
 */
export function StartPaidDialog({
  open,
  tenant,
  subscription,
  onClose,
}: {
  open: boolean;
  tenant: Pick<TenantDetail, 'id' | 'name'>;
  subscription: SubscriptionStatus;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const notify = useNotify();
  const plans = usePlans(open);
  const activePlans = (plans.data?.data ?? []).filter((p) => p.isActive);
  const [key, setKey] = useState(() => crypto.randomUUID());
  const [planId, setPlanId] = useState('');
  const [start, setStart] = useState<'after' | 'today'>('after');
  const [paymentRef, setPaymentRef] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const plan = activePlans.find((p) => p.id === planId);
  const left = subscription.daysRemaining;
  // One clock reading per opening keeps the preview stable while the form is filled in.
  const [now] = useState(() => Date.now());

  const close = () => {
    if (busy) return;
    setKey(crypto.randomUUID());
    setPlanId('');
    setStart('after');
    setPaymentRef('');
    setSubmitted(false);
    setError(null);
    onClose();
  };

  const submit = () => {
    setSubmitted(true);
    if (!plan || !paymentRef.trim()) return;
    setBusy(true);
    setError(null);
    unwrap(
      api.POST('/platform/tenants/{id}/subscription/extend', {
        params: { path: { id: tenant.id }, header: { 'idempotency-key': key } },
        body: {
          planId: plan.id,
          paymentRef: paymentRef.trim(),
          ...(start === 'today' ? { startToday: true } : {}),
          notes: `Paid subscription started (${plan.name})`,
        },
      }),
    )
      .then((r) => {
        setBusy(false);
        notify.success(
          `${tenant.name} is now on ${plan.name}, paid through ${businessDate(r.subscription?.paidThroughDate)}`,
        );
        void qc.invalidateQueries({ queryKey: tenantKeys.all });
        close();
      })
      .catch((e: unknown) => {
        setBusy(false);
        setError(e);
      });
  };

  const from = start === 'today' ? new Date(now).toISOString() : subscription.paidThrough;
  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Start paid subscription</DialogTitle>
        <DialogContent>
          <Stack spacing={2.25}>
            <Typography variant="body2" color="text.secondary">
              Use this when <strong>{tenant.name}</strong> has paid. The shop leaves its trial and
              moves to the plan you choose.
            </Typography>
            {error !== null && <Callout severity="error">{errorMessage(error)}</Callout>}
            <TextField
              select
              label="Plan"
              value={planId}
              onChange={(e) => setPlanId(e.target.value)}
              required
              error={submitted && !plan}
              helperText={
                submitted && !plan
                  ? 'Choose the plan the shop paid for.'
                  : activePlans.length === 0 && !plans.isPending
                    ? 'No active plans yet: create one under Plans first.'
                    : ' '
              }
            >
              {activePlans.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name} · {plural(p.durationDays, 'day')}
                  {p.maxUsers === null ? '' : ` · ${plural(p.maxUsers, 'user')}`}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Payment reference"
              value={paymentRef}
              onChange={(e) => setPaymentRef(e.target.value)}
              required
              placeholder="e.g. bank transfer, JazzCash or receipt no."
              slotProps={{ htmlInput: { maxLength: 100 } }}
              error={submitted && !paymentRef.trim()}
              helperText={
                submitted && !paymentRef.trim()
                  ? 'Enter how the shop paid, for the records.'
                  : 'Saved in the history and audit log.'
              }
            />
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Paid period starts
              </Typography>
              <RadioGroup
                value={start}
                onChange={(e) => setStart(e.target.value as 'after' | 'today')}
              >
                <FormControlLabel
                  value="after"
                  control={<Radio size="small" />}
                  label={
                    left > 0
                      ? `After the trial (keeps the ${plural(left, 'day')} left)`
                      : 'From today'
                  }
                />
                {left > 0 && (
                  <FormControlLabel
                    value="today"
                    control={<Radio size="small" />}
                    label="Today (the remaining trial days end now)"
                  />
                )}
              </RadioGroup>
            </Box>
            {plan && (
              <Callout severity="info" icon={<EventRepeatRounded fontSize="small" />}>
                Paid through about{' '}
                <strong>
                  {date(
                    start === 'today'
                      ? new Date(now + plan.durationDays * 86_400_000).toISOString()
                      : extendedPaidThrough(from, plan.durationDays).toISOString(),
                  )}
                </strong>
                {plan.maxUsers === null
                  ? ', with unlimited users.'
                  : `, with up to ${plural(plan.maxUsers, 'user')}.`}
              </Callout>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="outlined" onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" loading={busy}>
            Start paid plan
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

type Users = SubscriptionDetail['users'];

const LIMIT_SOURCE: Record<Users['source'], string> = {
  OVERRIDE: 'Set for this shop',
  TRIAL: 'Trial limit',
  PLAN: 'From the plan',
  UNLIMITED: 'No limit',
};

/** Raise or lower one shop's user limit, or return it to the plan's (or trial's) limit. */
function UserLimitDialog({
  open,
  tenantId,
  subscription,
  users,
  onClose,
}: {
  open: boolean;
  tenantId: string;
  subscription: SubscriptionStatus;
  users: Users;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const notify = useNotify();
  const [mode, setMode] = useState<'default' | 'custom'>(
    users.override === null ? 'default' : 'custom',
  );
  const [value, setValue] = useState(String(users.override ?? users.limit ?? 5));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const n = intIn(value, 1, 1000);
  const invalid = mode === 'custom' && n === null;
  const below = mode === 'custom' && n !== null && n < users.used;

  const submit = () => {
    if (invalid) return;
    setBusy(true);
    setError(null);
    unwrap(
      api.POST('/platform/tenants/{id}/subscription', {
        params: { path: { id: tenantId } },
        body: { version: subscription.version, maxUsersOverride: mode === 'custom' ? n : null },
      }),
    )
      .then((r) => {
        setBusy(false);
        notify.success(
          r.users.limit === null
            ? 'This shop can now add any number of users'
            : `This shop can now have up to ${plural(r.users.limit, 'user')}`,
        );
        void qc.invalidateQueries({ queryKey: tenantKeys.all });
        onClose();
      })
      .catch((e: unknown) => {
        setBusy(false);
        setError(e);
        if (e instanceof ApiError && e.code === 'VERSION_CONFLICT')
          void qc.invalidateQueries({ queryKey: tenantKeys.all });
      });
  };

  const defaultLabel = users.onTrial ? 'Use the trial limit' : 'Use the plan’s limit';
  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="xs" fullWidth>
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>User limit</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Users count active staff plus pending invitations. Now {users.used}
              {users.limit === null ? '' : ` of ${String(users.limit)}`}.
            </Typography>
            {error !== null && <Callout severity="error">{errorMessage(error)}</Callout>}
            <RadioGroup
              value={mode}
              onChange={(e) => setMode(e.target.value as 'default' | 'custom')}
            >
              <FormControlLabel
                value="default"
                control={<Radio size="small" />}
                label={defaultLabel}
              />
              <FormControlLabel
                value="custom"
                control={<Radio size="small" />}
                label="Set a limit for this shop only"
              />
            </RadioGroup>
            {mode === 'custom' && (
              <TextField
                label="Users allowed"
                value={value}
                onChange={(e) => setValue(e.target.value.replace(/\D/g, '').slice(0, 4))}
                slotProps={{ htmlInput: { inputMode: 'numeric' } }}
                error={invalid}
                helperText={
                  invalid ? 'Enter 1–1000.' : 'Stays until you change it, even after renewals.'
                }
                autoFocus
              />
            )}
            {below && (
              <Callout severity="warning">
                The shop already has {users.used}. Nobody is removed, but it cannot add users until
                it is below the new limit.
              </Callout>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="outlined" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" loading={busy} disabled={invalid}>
            Save limit
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function UsersPanel({
  users,
  manage,
  onEdit,
}: {
  users: Users;
  manage: boolean;
  onEdit: () => void;
}) {
  const pct = users.limit === null ? 0 : Math.min(100, (users.used / users.limit) * 100);
  const full = users.limit !== null && users.used >= users.limit;
  return (
    <Panel
      title="Users"
      subtitle="Active staff plus pending invitations count toward the limit."
      action={
        manage && (
          <Button size="small" variant="outlined" startIcon={<GroupsRounded />} onClick={onEdit}>
            Change limit
          </Button>
        )
      }
    >
      <Stack spacing={1.25}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
          <Typography variant="h4" component="span" className="tabular" sx={{ fontWeight: 700 }}>
            {users.used}
          </Typography>
          <Typography variant="body1" color="text.secondary" component="span">
            {users.limit === null ? 'users · unlimited' : `of ${plural(users.limit, 'user')}`}
          </Typography>
          <Chip
            size="small"
            variant="outlined"
            label={LIMIT_SOURCE[users.source]}
            sx={{ ml: 'auto' }}
          />
        </Stack>
        {users.limit !== null && (
          <LinearProgress
            variant="determinate"
            value={pct}
            color={full ? 'warning' : 'primary'}
            aria-label="Users used"
            sx={{ height: 8, borderRadius: 4 }}
          />
        )}
        <Typography variant="caption" color="text.secondary">
          {plural(users.activeUsers, 'active user')} ·{' '}
          {plural(users.pendingInvitations, 'pending invitation')}
          {full && ' · at the limit: the shop cannot add more users'}
        </Typography>
      </Stack>
    </Panel>
  );
}

/** Plan and grace period (the fields UpdateSubscriptionRequest allows), with optimistic version. */
function UpdateDialog({
  open,
  tenantId,
  subscription,
  onClose,
}: {
  open: boolean;
  tenantId: string;
  subscription: SubscriptionStatus;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const notify = useNotify();
  const plans = usePlans(open);
  const [planId, setPlanId] = useState(subscription.plan?.id ?? '');
  const [grace, setGrace] = useState(String(subscription.graceDays));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const graceN = intIn(grace, MIN_GRACE_DAYS, MAX_GRACE_DAYS);
  const options = (plans.data?.data ?? []).filter(
    (p) => p.isActive || p.id === subscription.plan?.id,
  );
  const planChanged = planId !== (subscription.plan?.id ?? '');
  const graceChanged = graceN !== null && graceN !== subscription.graceDays;

  const close = () => {
    if (busy) return;
    setError(null);
    onClose();
  };

  const submit = () => {
    if (graceN === null || (!planChanged && !graceChanged)) return;
    setBusy(true);
    setError(null);
    unwrap(
      api.POST('/platform/tenants/{id}/subscription', {
        params: { path: { id: tenantId } },
        body: {
          version: subscription.version,
          ...(planChanged ? { planId: planId || null } : {}),
          ...(graceChanged ? { graceDays: graceN } : {}),
        },
      }),
    )
      .then(() => {
        setBusy(false);
        notify.success('Subscription updated');
        void qc.invalidateQueries({ queryKey: tenantKeys.all });
        onClose();
      })
      .catch((e: unknown) => {
        setBusy(false);
        setError(e);
        if (e instanceof ApiError && e.code === 'VERSION_CONFLICT')
          void qc.invalidateQueries({ queryKey: tenantKeys.all });
      });
  };

  const conflict = error instanceof ApiError && error.code === 'VERSION_CONFLICT';

  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Edit subscription</DialogTitle>
        <DialogContent>
          <Stack spacing={2.25} sx={{ pt: 0.5 }}>
            {error !== null && (
              <Callout severity={conflict ? 'warning' : 'error'}>
                {conflict
                  ? 'Someone else changed this subscription. The latest values were loaded — close and try again.'
                  : errorMessage(error)}
              </Callout>
            )}
            <TextField
              select
              label="Plan"
              value={planId}
              onChange={(e) => setPlanId(e.target.value)}
              helperText="Changing the plan does not change the paid-through date."
            >
              <MenuItem value="">No plan</MenuItem>
              {options.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                  {!p.isActive && ' (inactive)'}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Grace period (days)"
              value={grace}
              onChange={(e) => setGrace(e.target.value)}
              slotProps={{ htmlInput: { inputMode: 'numeric' } }}
              error={graceN === null}
              helperText={
                graceN === null
                  ? `Between ${String(MIN_GRACE_DAYS)} and ${String(MAX_GRACE_DAYS)} days.`
                  : 'Days of full operation after the paid-through date before the shop locks.'
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="outlined" onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            loading={busy}
            disabled={graceN === null || (!planChanged && !graceChanged) || conflict}
          >
            Save changes
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

const DAY_MS = 86_400_000;

const entryName = (fix: CorrectableEvent) =>
  fix.event.kind === 'RENEWED' ? 'renewal' : 'extension';

/** Save a correction or deletion; the dialogs share the busy/error/version-conflict handling. */
function useEventFix(onClose: () => void) {
  const qc = useQueryClient();
  const notify = useNotify();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const run = (call: () => Promise<unknown>, done: string) => {
    setBusy(true);
    setError(null);
    call()
      .then(() => {
        setBusy(false);
        notify.success(done);
        void qc.invalidateQueries({ queryKey: tenantKeys.all });
        onClose();
      })
      .catch((e: unknown) => {
        setBusy(false);
        setError(e);
        if (e instanceof ApiError && e.code === 'VERSION_CONFLICT')
          void qc.invalidateQueries({ queryKey: tenantKeys.all });
      });
  };
  const conflict = error instanceof ApiError && error.code === 'VERSION_CONFLICT';
  const callout = error !== null && (
    <Callout severity={conflict ? 'warning' : 'error'}>
      {conflict
        ? 'Someone else changed this subscription. The latest values were loaded — close and try again.'
        : errorMessage(error)}
    </Callout>
  );
  return { busy, conflict, callout, run };
}

/** Fix the days, payment reference or notes of the latest renewal or extension. */
function EditEventDialog({
  tenantId,
  subscription,
  fix,
  onClose,
}: {
  tenantId: string;
  subscription: SubscriptionStatus;
  fix: CorrectableEvent;
  onClose: () => void;
}) {
  const f = useEventFix(onClose);
  const [days, setDays] = useState(String(fix.days));
  const [paymentRef, setPaymentRef] = useState(fix.paymentRef ?? '');
  const [notes, setNotes] = useState(fix.notes ?? '');
  const [reason, setReason] = useState('');
  const n = intIn(days, 1, 3660);
  const start = Date.parse(fix.paidThroughAfter) - fix.days * DAY_MS;
  const after = n === null ? null : new Date(start + n * DAY_MS).toISOString();
  const changed =
    n !== fix.days ||
    paymentRef.trim() !== (fix.paymentRef ?? '') ||
    notes.trim() !== (fix.notes ?? '');
  const invalid = n === null || !reason.trim() || !changed;

  const submit = () => {
    if (invalid) return;
    f.run(
      () =>
        unwrap(
          api.PATCH('/platform/tenants/{id}/subscription/events/{eventId}', {
            params: { path: { id: tenantId, eventId: fix.event.id } },
            body: {
              version: subscription.version,
              ...(n !== fix.days ? { days: n } : {}),
              paymentRef: paymentRef.trim() || null,
              notes: notes.trim() || null,
              reason: reason.trim(),
            },
          }),
        ),
      'Entry corrected',
    );
  };

  return (
    <Dialog open onClose={() => !f.busy && onClose()} maxWidth="xs" fullWidth>
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Edit {entryName(fix)}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.25} sx={{ pt: 0.5 }}>
            {f.callout}
            <TextField
              label="Days"
              value={days}
              onChange={(e) => setDays(e.target.value.replace(/\D/g, '').slice(0, 4))}
              slotProps={{ htmlInput: { inputMode: 'numeric' } }}
              error={n === null}
              helperText={
                n === null
                  ? 'Enter 1–3660.'
                  : `Paid through becomes ${date(after ?? fix.paidThroughAfter)} (now ${date(fix.paidThroughAfter)}).`
              }
              autoFocus
            />
            <TextField
              label="Payment reference"
              value={paymentRef}
              onChange={(e) => setPaymentRef(e.target.value)}
              slotProps={{ htmlInput: { maxLength: 100 } }}
            />
            <TextField
              label="Notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              minRows={2}
              slotProps={{ htmlInput: { maxLength: 500 } }}
            />
            <TextField
              label="Reason for the change"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              helperText="Kept in the audit log; the original entry stays in the history."
              slotProps={{ htmlInput: { maxLength: 500 } }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="outlined" onClick={onClose} disabled={f.busy}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            loading={f.busy}
            disabled={invalid || f.conflict}
          >
            Save correction
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

/** Take back a renewal or extension added by mistake. */
function DeleteEventDialog({
  tenantId,
  subscription,
  fix,
  onClose,
}: {
  tenantId: string;
  subscription: SubscriptionStatus;
  fix: CorrectableEvent;
  onClose: () => void;
}) {
  const f = useEventFix(onClose);
  const [reason, setReason] = useState('');
  const renewal = fix.event.kind === 'RENEWED';

  const submit = () => {
    if (!reason.trim()) return;
    f.run(
      () =>
        unwrap(
          api.POST('/platform/tenants/{id}/subscription/events/{eventId}/reverse', {
            params: { path: { id: tenantId, eventId: fix.event.id } },
            body: { version: subscription.version, reason: reason.trim() },
          }),
        ),
      'Entry deleted',
    );
  };

  return (
    <Dialog open onClose={() => !f.busy && onClose()} maxWidth="xs" fullWidth>
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete this {entryName(fix)}?</DialogTitle>
        <DialogContent>
          <Stack spacing={2.25} sx={{ pt: 0.5 }}>
            {f.callout}
            <Typography variant="body2">
              Paid through goes back from <strong>{date(fix.paidThroughAfter)}</strong> to{' '}
              <strong>{date(fix.paidThroughBefore)}</strong>
              {renewal ? ', and the plan back to the one before this renewal' : ''}.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              The entry stays in the history, marked deleted, and no longer counts as a payment.
            </Typography>
            <TextField
              label="Reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              autoFocus
              placeholder="e.g. Added to the wrong shop"
              slotProps={{ htmlInput: { maxLength: 500 } }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="outlined" onClick={onClose} disabled={f.busy}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="error"
            loading={f.busy}
            disabled={!reason.trim() || f.conflict}
          >
            Delete entry
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function PaidChange({ e }: { e: SubscriptionEvent }) {
  if (e.paidThroughBefore === e.paidThroughAfter) return <>{date(e.paidThroughAfter)}</>;
  return (
    <span className="tabular" style={{ whiteSpace: 'nowrap' }}>
      {date(e.paidThroughBefore)} → <strong>{date(e.paidThroughAfter)}</strong>
    </span>
  );
}

const ACTORS: Record<SubscriptionEvent['actorType'], string> = {
  USER: 'Shop user',
  PLATFORM_ADMIN: 'Platform admin',
  SYSTEM: 'System',
};

const historyColumns = (
  history: SubscriptionEvent[],
  fix: { id: string; edit: () => void; remove: () => void } | null,
): Column<SubscriptionEvent>[] => {
  const reversed = reversedIds(history);
  const edited = new Set(
    history.flatMap((e) =>
      e.kind === 'CORRECTED' && e.correctsEventId ? [e.correctsEventId] : [],
    ),
  );
  const columns: Column<SubscriptionEvent>[] = [
    {
      key: 'kind',
      header: 'Event',
      render: (e) => (
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
          <Chip
            size="small"
            variant="outlined"
            label={EVENT_LABELS[e.kind]}
            sx={{
              fontWeight: 600,
              ...(reversed.has(e.id) ? { textDecoration: 'line-through', opacity: 0.6 } : {}),
            }}
          />
          {reversed.has(e.id) ? (
            <Chip size="small" color="error" label="Deleted" />
          ) : (
            edited.has(e.id) && <Chip size="small" color="warning" label="Edited" />
          )}
        </Stack>
      ),
    },
    {
      key: 'days',
      header: 'Days',
      align: 'right',
      render: (e) => (
        <span className="tabular">{e.daysAdded === null ? '—' : `+${String(e.daysAdded)}`}</span>
      ),
    },
    { key: 'paid', header: 'Paid through', hideBelow: 'sm', render: (e) => <PaidChange e={e} /> },
    {
      key: 'by',
      header: 'By',
      hideBelow: 'md',
      render: (e) => ACTORS[e.actorType],
    },
    {
      key: 'notes',
      header: 'Notes',
      hideBelow: 'md',
      render: (e) => (
        <Box sx={{ maxWidth: 280 }}>
          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
            {e.notes ?? '—'}
          </Typography>
          {e.paymentRef && (
            <Typography variant="caption" color="text.secondary">
              Ref {e.paymentRef}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: 'when',
      header: 'When',
      render: (e) => (
        <span className="tabular" style={{ whiteSpace: 'nowrap' }}>
          {dateTime(e.createdAt)}
        </span>
      ),
    },
  ];
  if (!fix) return columns;
  return [
    ...columns,
    {
      key: 'fix',
      header: '',
      align: 'right',
      render: (e) =>
        e.id === fix.id && (
          <Stack direction="row" spacing={0.25} sx={{ justifyContent: 'flex-end' }}>
            <Tooltip title="Edit this entry">
              <IconButton size="small" aria-label="Edit entry" onClick={fix.edit}>
                <EditRounded fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete this entry">
              <IconButton size="small" color="error" aria-label="Delete entry" onClick={fix.remove}>
                <DeleteOutlineRounded fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
    },
  ];
};

export function SubscriptionTab({ tenant }: { tenant: TenantDetail }) {
  const { can } = useAuth();
  const manage = can('subscriptions.manage');
  const [extending, setExtending] = useState(false);
  const [editing, setEditing] = useState(false);
  const [startingPaid, setStartingPaid] = useState(false);
  const [limitOpen, setLimitOpen] = useState(false);
  const [fixing, setFixing] = useState<'edit' | 'delete' | null>(null);
  const sub = useQuery({
    queryKey: tenantKeys.subscription(tenant.id),
    queryFn: () => fetchSubscription(tenant.id),
  });

  if (sub.isPending) return <LoadingRows rows={5} height={56} />;
  if (sub.isError)
    return (
      <Card>
        <ErrorState error={sub.error} onRetry={() => void sub.refetch()} dense />
      </Card>
    );

  const s = sub.data;
  const trial = isTrial(s.history);
  const status = s.suspended ? 'SUSPENDED' : s.state;
  const fix = manage ? correctableEvent(s.history) : null;

  return (
    <Stack spacing={2.5}>
      <Panel
        title="Current period"
        subtitle={`Dates in the shop’s time zone (${s.timeZone})`}
        action={
          manage && (
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<EditRounded />}
                onClick={() => setEditing(true)}
              >
                Edit
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<EventRepeatRounded />}
                onClick={() => setExtending(true)}
              >
                Extend
              </Button>
            </Stack>
          )
        }
      >
        <FieldGrid>
          <Field label="Status">
            <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
              <StatusChipFor status={status} />
              {trial && <StatusChip status="TRIAL" label="Trial" />}
            </Stack>
          </Field>
          <Field label="Plan">{s.plan?.name ?? 'No plan'}</Field>
          <Field label="Started">{date(s.startsAt)}</Field>
          <Field label="Paid through">
            {businessDate(s.paidThroughDate)}
            <Typography variant="caption" color="text.secondary" component="div">
              {plural(s.daysRemaining, 'day')} remaining
            </Typography>
          </Field>
          <Field label="Grace period">{plural(s.graceDays, 'day')}</Field>
          <Field label="Locks on">
            {businessDate(s.graceEndsDate)}
            <Typography variant="caption" color="text.secondary" component="div">
              {s.locked ? 'Locked now' : `in ${plural(s.daysUntilLock, 'day')}`}
            </Typography>
          </Field>
        </FieldGrid>
        {trial && !s.suspended && (
          <Callout severity="info" mt={2.5} icon={<WorkspacePremiumRounded fontSize="small" />}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between' }}
            >
              <span>
                On its free trial ({plural(s.daysRemaining, 'day')} left). When the shop pays, start
                its paid subscription here.
              </span>
              {manage && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<WorkspacePremiumRounded />}
                  onClick={() => setStartingPaid(true)}
                  sx={{ flexShrink: 0 }}
                >
                  Start paid subscription
                </Button>
              )}
            </Stack>
          </Callout>
        )}
        {s.suspended && (
          <Callout severity="error" mt={2.5}>
            Suspended by the platform: the shop is locked regardless of its dates until it is
            resumed.
          </Callout>
        )}
      </Panel>

      <UsersPanel users={s.users} manage={manage} onEdit={() => setLimitOpen(true)} />

      <Box>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1.5 }}>
          <HistoryRounded fontSize="small" color="action" />
          <Typography variant="h5">History</Typography>
        </Stack>
        <DataTable
          columns={historyColumns(
            s.history,
            fix && {
              id: fix.event.id,
              edit: () => setFixing('edit'),
              remove: () => setFixing('delete'),
            },
          )}
          rows={s.history}
          rowKey={(e) => e.id}
          empty={
            <EmptyState
              dense
              icon={<HistoryRounded />}
              title="No events yet"
              body="Renewals, extensions, redeemed codes and suspensions are listed here."
            />
          }
        />
      </Box>

      {manage && (
        <>
          <ExtendDialog
            open={extending}
            tenant={tenant}
            subscription={s}
            onClose={() => setExtending(false)}
          />
          <StartPaidDialog
            open={startingPaid}
            tenant={tenant}
            subscription={s}
            onClose={() => setStartingPaid(false)}
          />
          {limitOpen && (
            <UserLimitDialog
              open
              tenantId={tenant.id}
              subscription={s}
              users={s.users}
              onClose={() => setLimitOpen(false)}
            />
          )}
          {fix && fixing === 'edit' && (
            <EditEventDialog
              tenantId={tenant.id}
              subscription={s}
              fix={fix}
              onClose={() => setFixing(null)}
            />
          )}
          {fix && fixing === 'delete' && (
            <DeleteEventDialog
              tenantId={tenant.id}
              subscription={s}
              fix={fix}
              onClose={() => setFixing(null)}
            />
          )}
          {editing && (
            <UpdateDialog
              open
              tenantId={tenant.id}
              subscription={s}
              onClose={() => setEditing(false)}
            />
          )}
        </>
      )}
    </Stack>
  );
}
